/**
 * Persistent storage protection.
 *
 * All schedule data lives in IndexedDB on this one device with no backup, so
 * browser-initiated eviction (Safari under storage pressure, Chromium's
 * clearing of unused origins) would silently destroy the schedule. Asking for
 * persistence marks the origin's storage as not-evictable on granting
 * browsers. Chrome decides by heuristic (installation helps), Safari grants
 * it for home-screen apps, and users can always still clear data manually.
 */

export type PersistenceState = 'protected' | 'best-effort' | 'unsupported';

/** Requests persistence once; safe to call on every launch (idempotent). */
export async function requestPersistentStorage(): Promise<PersistenceState> {
  try {
    if (!('storage' in navigator) || typeof navigator.storage.persist !== 'function') {
      return 'unsupported';
    }
    return (await navigator.storage.persist()) ? 'protected' : 'best-effort';
  } catch {
    // A storage API failure must never break app startup.
    return 'best-effort';
  }
}

/** Current protection state, without prompting for anything. */
export async function persistenceState(): Promise<PersistenceState> {
  try {
    if (!('storage' in navigator) || typeof navigator.storage.persisted !== 'function') {
      return 'unsupported';
    }
    return (await navigator.storage.persisted()) ? 'protected' : 'best-effort';
  } catch {
    return 'best-effort';
  }
}

export interface StorageUsage {
  readonly usageBytes: number;
  readonly quotaBytes: number;
}

/** Storage used and available, when the browser reports it. */
export async function storageUsage(): Promise<StorageUsage | null> {
  try {
    if (!('storage' in navigator) || typeof navigator.storage.estimate !== 'function') {
      return null;
    }
    const estimate = await navigator.storage.estimate();
    if (estimate.usage === undefined || estimate.quota === undefined) {
      return null;
    }
    return { usageBytes: estimate.usage, quotaBytes: estimate.quota };
  } catch {
    return null;
  }
}
